import React, { useState, useEffect } from 'react';
import { getMustardLogs, saveMustardBatch, fileMustardBatch, deleteMustardBatch,
         getGarlicDipLogs, saveGarlicDipBatch, fileGarlicDipBatch, deleteGarlicDipBatch } from '../../../services/mustardDipService';
import './MustardDip.css';

const MUSTARD_FLAVORS = ['Spicy Brown', 'Roasted Garlic', 'Cranberry Spice'];

function toDateInput(val) {
  if (!val) return '';
  return String(val).slice(0, 10);
}

export default function MustardDip() {
  const [activeTab, setActiveTab] = useState('mustard');
  const [mustardBatches, setMustardBatches] = useState([]);
  const [dipBatches, setDipBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingBatch, setEditingBatch] = useState(null);
  const [showArchive, setShowArchive] = useState(false);

  useEffect(() => { loadAll(); }, []);

  async function loadAll() {
    setLoading(true);
    try {
      const [mRes, dRes] = await Promise.all([getMustardLogs(), getGarlicDipLogs()]);
      if (mRes.success) setMustardBatches(mRes.data.sort((a, b) => new Date(b.batchDate) - new Date(a.batchDate)));
      if (dRes.success) setDipBatches(dRes.data.sort((a, b) => new Date(b.batchDate) - new Date(a.batchDate)));
    } catch (e) { console.error(e); }
    setLoading(false);
  }

  async function handleFile(batch, type) {
    if (!window.confirm(`File this ${type} batch?`)) return;
    const res = type === 'mustard' ? await fileMustardBatch(batch.batchId) : await fileGarlicDipBatch(batch.batchId);
    if (res.success) loadAll();
    else alert('Error: ' + res.error);
  }

  async function handleDelete(batch, type) {
    if (!window.confirm(`Delete this ${type} batch?`)) return;
    const res = type === 'mustard' ? await deleteMustardBatch(batch.batchId) : await deleteGarlicDipBatch(batch.batchId);
    if (res.success) loadAll();
    else alert('Error: ' + res.error);
  }

  const activeMustard = mustardBatches.filter(b => b.filed !== 'yes');
  const filedMustard = mustardBatches.filter(b => b.filed === 'yes');
  const activeDip = dipBatches.filter(b => b.filed !== 'yes');
  const filedDip = dipBatches.filter(b => b.filed === 'yes');

  return (
    <div className="md-container">
      <div className="md-header">
        <h1>🫙 Mustard & Garlic Dip Log</h1>
        <button className="md-btn-primary" onClick={() => { setEditingBatch(null); setShowForm(true); }}>
          + New Batch
        </button>
      </div>

      <div className="md-tabs">
        {['mustard', 'garlicDip'].map(tab => (
          <button key={tab} className={`md-tab ${activeTab === tab ? 'active' : ''}`} onClick={() => { setActiveTab(tab); setShowForm(false); }}>
            {tab === 'mustard' ? '🌭 Mustard' : '🧄 Garlic Dip'}
          </button>
        ))}
      </div>

      {showForm && (
        activeTab === 'mustard'
          ? <MustardForm batch={editingBatch} onSaved={() => { setShowForm(false); loadAll(); }} onCancel={() => setShowForm(false)} />
          : <GarlicDipForm batch={editingBatch} onSaved={() => { setShowForm(false); loadAll(); }} onCancel={() => setShowForm(false)} />
      )}

      {loading ? <div className="md-loading">Loading...</div> : (
        <div className="md-batch-list">
          <h2 className="md-section-title">🟢 Active Batches</h2>
          {(activeTab === 'mustard' ? activeMustard : activeDip).length === 0 ? (
            <div className="md-empty">No active batches.</div>
          ) : (
            (activeTab === 'mustard' ? activeMustard : activeDip).map(batch => (
              <BatchCard
                key={batch.batchId}
                batch={batch}
                type={activeTab}
                onEdit={() => { setEditingBatch(batch); setShowForm(true); }}
                onFile={() => handleFile(batch, activeTab)}
                onDelete={() => handleDelete(batch, activeTab)}
              />
            ))
          )}

          <div className="md-archive-toggle" onClick={() => setShowArchive(!showArchive)}>
            📁 Archive ({(activeTab === 'mustard' ? filedMustard : filedDip).length}) {showArchive ? '▲' : '▼'}
          </div>
          {showArchive && (activeTab === 'mustard' ? filedMustard : filedDip).map(batch => (
            <BatchCard
              key={batch.batchId}
              batch={batch}
              type={activeTab}
              archived
              onEdit={() => { setEditingBatch(batch); setShowForm(true); }}
              onDelete={() => handleDelete(batch, activeTab)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function BatchCard({ batch, type, archived, onEdit, onFile, onDelete }) {
  const isMustard = type === 'mustard';
  const phOk = !batch.pH || parseFloat(batch.pH) <= 4.2;
  const cookOk = !batch.cookTemp || parseFloat(batch.cookTemp) >= 200;
  const fillOk = !batch.fillTemp || parseFloat(batch.fillTemp) >= 195;
  const compliant = phOk && cookOk && fillOk;

  return (
    <div className={`md-batch-card ${archived ? 'archived' : ''}`}>
      <div className="md-batch-body">
        <div className="md-batch-top">
          <span className="md-batch-id">{isMustard ? batch.flavor : 'Garlic Dip'}</span>
          <span className="md-batch-date">{batch.batchDate ? String(batch.batchDate).slice(0,10) : '—'}</span>
          {archived && <span className="md-filed-badge">Filed</span>}
          {isMustard && !archived && (
            <span className={`md-compliance-badge ${compliant ? 'ok' : 'warn'}`}>
              {compliant ? '✅ Compliant' : '⚠️ Check Values'}
            </span>
          )}
        </div>
        <div className="md-batch-meta">
          {isMustard ? (
            <>
              <span>{batch.jarCount || '—'} jars</span>
              <span>pH: {batch.pH || '—'}{batch.pH && !phOk ? ' ⚠️' : ''}</span>
              <span>Cook: {batch.cookTemp || '—'}°F{batch.cookTemp && !cookOk ? ' ⚠️' : ''}</span>
              <span>Fill: {batch.fillTemp || '—'}°F{batch.fillTemp && !fillOk ? ' ⚠️' : ''}</span>
            </>
          ) : (
            <span>{batch.batchSize || '—'} {batch.unit || ''}</span>
          )}
          <span>Exp: {batch.expirationDate ? String(batch.expirationDate).slice(0,10) : '—'}</span>
        </div>
        {batch.notes && <div className="md-batch-notes">{batch.notes}</div>}
      </div>
      <div className="md-batch-actions">
        <button className="md-btn-edit" onClick={onEdit}>Edit</button>
        {!archived && onFile && <button className="md-btn-file" onClick={onFile}>File</button>}
        <button className="md-btn-delete" onClick={onDelete}>Delete</button>
      </div>
    </div>
  );
}

function MustardForm({ batch, onSaved, onCancel }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    batchId: batch?.batchId || '',
    batchDate: toDateInput(batch?.batchDate) || new Date().toISOString().slice(0,10),
    expirationDate: toDateInput(batch?.expirationDate) || '',
    flavor: batch?.flavor || MUSTARD_FLAVORS[0],
    jarCount: batch?.jarCount || '',
    pH: batch?.pH || '',
    cookTemp: batch?.cookTemp || '',
    cookDuration: batch?.cookDuration || '6',
    fillTemp: batch?.fillTemp || '',
    loggedBy: batch?.loggedBy || '',
    notes: batch?.notes || '',
    filed: batch?.filed || '',
  });

  function handleChange(e) {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  }

  const phOk = !form.pH || parseFloat(form.pH) <= 4.2;
  const cookOk = !form.cookTemp || parseFloat(form.cookTemp) >= 200;
  const fillOk = !form.fillTemp || parseFloat(form.fillTemp) >= 195;

  async function handleSave() {
    if (!form.batchDate) return alert('Batch date is required.');
    setSaving(true);
    const res = await saveMustardBatch(form);
    setSaving(false);
    if (res.success) onSaved();
    else alert('Error: ' + res.error);
  }

  return (
    <div className="md-form">
      <div className="md-form-header">
        <h3>{batch ? 'Edit Mustard Batch' : 'New Mustard Batch'}</h3>
        <button className="md-btn-secondary" onClick={onCancel}>Cancel</button>
      </div>
      <div className="md-form-row">
        <div className="md-form-group">
          <label>Batch Date</label>
          <input type="date" name="batchDate" value={form.batchDate} onChange={handleChange} />
        </div>
        <div className="md-form-group">
          <label>Expiration Date</label>
          <input type="date" name="expirationDate" value={form.expirationDate} onChange={handleChange} />
        </div>
        <div className="md-form-group">
          <label>Flavor</label>
          <select name="flavor" value={form.flavor} onChange={handleChange}>
            {MUSTARD_FLAVORS.map(f => <option key={f} value={f}>{f}</option>)}
          </select>
        </div>
        <div className="md-form-group">
          <label>Jar Count</label>
          <input type="number" name="jarCount" value={form.jarCount} onChange={handleChange} placeholder="0" />
        </div>
      </div>

      <div className="md-compliance-section">
        <div className="md-form-section-title">Compliance Records</div>
        <div className="md-form-row">
          <div className="md-form-group">
            <label>pH {form.pH && <span className={phOk ? 'md-ok' : 'md-warn'}>{phOk ? '✅ ≤4.2' : '⚠️ EXCEEDS 4.2'}</span>}</label>
            <input type="number" step="0.01" name="pH" value={form.pH} onChange={handleChange} placeholder="Target: ≤4.2" />
          </div>
          <div className="md-form-group">
            <label>Cook Temp (°F) {form.cookTemp && <span className={cookOk ? 'md-ok' : 'md-warn'}>{cookOk ? '✅ ≥200°F' : '⚠️ BELOW 200°F'}</span>}</label>
            <input type="number" step="0.1" name="cookTemp" value={form.cookTemp} onChange={handleChange} placeholder="Minimum: 200°F" />
          </div>
          <div className="md-form-group">
            <label>Cook Duration (min)</label>
            <input type="number" step="0.5" name="cookDuration" value={form.cookDuration} onChange={handleChange} placeholder="Minimum: 6 min" />
          </div>
          <div className="md-form-group">
            <label>Fill Temp (°F) {form.fillTemp && <span className={fillOk ? 'md-ok' : 'md-warn'}>{fillOk ? '✅ ≥195°F' : '⚠️ BELOW 195°F'}</span>}</label>
            <input type="number" step="0.1" name="fillTemp" value={form.fillTemp} onChange={handleChange} placeholder="Minimum: 195°F" />
          </div>
        </div>
      </div>

      <div className="md-form-row">
        <div className="md-form-group">
          <label>Logged By</label>
          <input type="text" name="loggedBy" value={form.loggedBy} onChange={handleChange} placeholder="Name" />
        </div>
        <div className="md-form-group" style={{flex: 2}}>
          <label>Notes</label>
          <input type="text" name="notes" value={form.notes} onChange={handleChange} placeholder="Any notes..." />
        </div>
      </div>

      <div className="md-form-actions">
        <button className="md-btn-secondary" onClick={onCancel}>Cancel</button>
        <button className="md-btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save Batch'}
        </button>
      </div>
    </div>
  );
}

function GarlicDipForm({ batch, onSaved, onCancel }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    batchId: batch?.batchId || '',
    batchDate: toDateInput(batch?.batchDate) || new Date().toISOString().slice(0,10),
    expirationDate: toDateInput(batch?.expirationDate) || '',
    batchSize: batch?.batchSize || '',
    unit: batch?.unit || 'jars',
    loggedBy: batch?.loggedBy || '',
    notes: batch?.notes || '',
    filed: batch?.filed || '',
  });

  function handleChange(e) {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  }

  async function handleSave() {
    if (!form.batchDate) return alert('Batch date is required.');
    setSaving(true);
    const res = await saveGarlicDipBatch(form);
    setSaving(false);
    if (res.success) onSaved();
    else alert('Error: ' + res.error);
  }

  return (
    <div className="md-form">
      <div className="md-form-header">
        <h3>{batch ? 'Edit Garlic Dip Batch' : 'New Garlic Dip Batch'}</h3>
        <button className="md-btn-secondary" onClick={onCancel}>Cancel</button>
      </div>
      <div className="md-form-row">
        <div className="md-form-group">
          <label>Batch Date</label>
          <input type="date" name="batchDate" value={form.batchDate} onChange={handleChange} />
        </div>
        <div className="md-form-group">
          <label>Expiration Date</label>
          <input type="date" name="expirationDate" value={form.expirationDate} onChange={handleChange} />
        </div>
        <div className="md-form-group">
          <label>Batch Size</label>
          <input type="number" name="batchSize" value={form.batchSize} onChange={handleChange} placeholder="0" />
        </div>
        <div className="md-form-group">
          <label>Unit</label>
          <select name="unit" value={form.unit} onChange={handleChange}>
            <option value="jars">Jars</option>
            <option value="lbs">Lbs</option>
            <option value="cups">Cups</option>
          </select>
        </div>
      </div>
      <div className="md-form-row">
        <div className="md-form-group">
          <label>Logged By</label>
          <input type="text" name="loggedBy" value={form.loggedBy} onChange={handleChange} placeholder="Name" />
        </div>
        <div className="md-form-group" style={{flex: 2}}>
          <label>Notes</label>
          <input type="text" name="notes" value={form.notes} onChange={handleChange} placeholder="Any notes..." />
        </div>
      </div>
      <div className="md-form-actions">
        <button className="md-btn-secondary" onClick={onCancel}>Cancel</button>
        <button className="md-btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save Batch'}
        </button>
      </div>
    </div>
  );
}
